// src/components/Leaderboard.js
import React, { useState, useEffect } from 'react';
import api from '../api';
import Avatar from "../components/Avatar";
import { ChevronDownIcon, ChevronUpIcon, ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/solid';
import { motion, AnimatePresence } from 'framer-motion';

export default function Leaderboard() {
  const [leaders, setLeaders] = useState([]);
  const [expandedUser, setExpandedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    setLoading(true);
    api.get('/leaderboard')
      .then(({ data }) => {
        const rows = data.map((u, i) => ({
          rank: i + 1,
          userId: u.userId,
          emailPrefix: u.emailPrefix,
          roi: u.roi.toFixed(2),
          wins: u.wins,
          chips: u.chips,
          joinedAt: u.joinedAt,
          referralCode: u.referralCode,
        }));
        setLeaders(rows);
        setLoading(false);
      })
      .catch(error => {
        console.error(error);
        setLoading(false);
      });
  }, []);

  // Toggle expanded user profile
  const toggleExpandUser = (userId) => {
    if (expandedUser === userId) {
      setExpandedUser(null);
    } else {
      setExpandedUser(userId);
    }
  };

  // Function to render rank indicator based on position
  const renderRankIndicator = (rank) => {
    if (rank === 1) {
      // First place medal image
      return (
        <img 
        src="/a.png" 
          alt="1st Place" 
          className="w-6 h-6 sm:w-8 sm:h-8"
        />
      );
    } else if (rank === 2) {
      // Second place medal image
      return (
        <img 
        src="/b.png" 
          alt="2nd Place" 
          className="w-6 h-6 sm:w-8 sm:h-8"
        />
      );
    } else if (rank === 3) {
      // Third place medal image
      return (
        <img 
          src="/c.png" 
          alt="3rd Place" 
          className="w-6 h-6 sm:w-8 sm:h-8"
        />
      );
    } else {
      // Standard rank styling
      return (
        <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gray-800 flex items-center justify-center text-gray-300 text-xs sm:text-base">
          {rank}
        </div>
      );
    }
  };

  // Section header component
  const LeaderboardHeader = () => (
    <div className="flex items-center mb-6">
      <h3 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">
        Leaderboard
      </h3>
      <div className="flex-grow h-px ml-4 bg-gradient-to-r from-violet-500/50 via-pink-500/50 to-transparent" />
      <div className="ml-2 px-2 py-1 bg-black bg-opacity-40 rounded text-xs text-pink-400 border border-pink-500/30">
        Live
      </div>
    </div>
  );

  // Loading state
  if (loading) {
    return (
      <section id="leaderboard" className="-mt-8 my-8 font-mono">
        <LeaderboardHeader />
        
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 border-2 border-violet-500 rounded-full relative flex items-center justify-center">
              <div className="w-10 h-10 bg-gradient-to-r from-violet-500 via-pink-500 to-red-500 rounded-full animate-pulse"></div>
              <div className="absolute inset-0 border-t-2 border-violet-500 rounded-full animate-spin"></div>
            </div>
            <div className="mt-4 font-mono text-sm">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">
                Loading Leaderboard...
              </span>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // No records state
  if (leaders.length === 0) {
    return (
      <section id="leaderboard" className="-mt-8 my-8 font-mono">
        <LeaderboardHeader />
        
        <div className="mb-12 pt-4 mt-8 animate-gradient-border p-0.5 rounded-2xl">
          <div className="bg-gray-900/90 backdrop-blur-sm rounded-2xl shadow-lg overflow-hidden p-8">
            <div className="flex flex-col items-center justify-center text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-violet-900/30 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-violet-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h4 className="text-lg font-medium text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">
                No players on the leaderboard yet
              </h4>
              <p className="text-gray-400 text-sm max-w-md">
                Be the first to make your mark! Play a few games to secure your spot on the leaderboard.
              </p>
            </div>
          </div>
        </div>
        
        <div className="text-xs text-gray-400 italic -mt-4 mb-4 text-center">
          tap in, stats out.
        </div>
      </section>
    );
  }

  return (
    <section id="leaderboard" className="-mt-8 my-8 font-mono">
      <LeaderboardHeader />

      {/* Elegant Table Leaderboard */}
      <div className="mb-12 pt-4 mt-8 animate-gradient-border p-0.5 rounded-2xl">
        <div className="bg-gray-900/90 backdrop-blur-sm rounded-2xl shadow-lg overflow-hidden">
          {/* Table Header */}
          <div className="px-3 sm:px-6 py-4 border-b border-gray-700/50 bg-black/30">
            <div className="grid grid-cols-10 gap-1 sm:gap-2">
              <div className="col-span-2 text-xs font-semibold uppercase text-gray-400 text-center">Rank</div>
              <div className="col-span-4 text-xs font-semibold uppercase text-gray-400 text-center">Player</div>
              <div className="col-span-2 text-xs font-semibold uppercase text-gray-400 text-center">ROI</div>
              <div className="col-span-2 text-xs font-semibold uppercase text-gray-400 text-center">Wins</div>
            </div>
          </div>
          
          {/* Table Body */}
          <div>
            {leaders.map((leader, index) => {
              // Determine if ROI is positive or negative
              const isPositiveROI = parseFloat(leader.roi) >= 0;
              const isExpanded = expandedUser === leader.userId;
              
              return (
                <React.Fragment key={leader.userId}>
                  {/* Main Row */}
                  <div 
                    className={`border-b border-gray-800/50 transition-colors duration-200 hover:bg-violet-900/10 ${
                      index % 2 === 0 ? 'bg-black/20' : ''
                    }`}
                  >
                    <div 
                      className="px-3 sm:px-6 py-3 sm:py-4 grid grid-cols-10 gap-1 sm:gap-2 items-center cursor-pointer"
                      onClick={() => toggleExpandUser(leader.userId)}
                    >
                      {/* Rank */}
                      <div className="col-span-2 flex justify-center">
                        {renderRankIndicator(leader.rank)}
                      </div>
                      
                      {/* Player */}
                      <div className="col-span-4 flex items-center justify-center">
                        <div className="font-medium text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400 text-sm sm:text-base truncate">
                          {leader.emailPrefix}
                        </div>
                      </div>
                      
                      {/* ROI */}
                      <div className="col-span-2 flex justify-center">
                        <div className={`flex items-center space-x-1 ${
                          isPositiveROI ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {isPositiveROI ? (
                            <ArrowUpIcon className="h-2 w-2 sm:h-3 sm:w-3" />
                          ) : (
                            <ArrowDownIcon className="h-2 w-2 sm:h-3 sm:w-3" />
                          )}
                          <span className="font-medium text-xs sm:text-base">{leader.roi}%</span>
                        </div>
                      </div>
                      
                      {/* Wins */}
                      <div className="col-span-2 text-center">
                        <span className="font-medium text-white text-xs sm:text-base">{leader.wins}</span>
                      </div>
                    </div>
                    
                    {/* Expandable detail row */}
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.2 }}
                          className="bg-gradient-to-r from-violet-900/20 to-transparent border-t border-violet-500/20"
                        >
                          <motion.div 
                            initial={{ y: -10 }}
                            animate={{ y: 0 }}
                            exit={{ y: -10 }}
                            className="px-3 sm:px-6 py-3 sm:py-4"
                          >
                            {/* Centered User Details */}
                            <div className="flex items-center justify-center space-x-4">
                              {/* Avatar with gradient border */}
                              <div className="p-0.5 rounded-full">
                                <Avatar
                                  seed={leader.referralCode}
                                  size={40}
                                  className="rounded-full"
                                />
                              </div>

                              {/* Stats beside the avatar */}
                              <div className="flex items-center space-x-4">
                                <div className="text-xs sm:text-sm text-gray-400">
                                  Joined: <span className="text-white">{new Date(leader.joinedAt).toLocaleDateString()}</span>
                                </div>
                                <div className="h-4 w-px bg-gray-700/50"></div>
                                <div className="text-xs sm:text-sm text-gray-400">
                                  Chips: <span className="text-white">{leader.chips.toLocaleString()}</span>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* Helper Text */}
      <div className="text-xs text-gray-400 italic -mt-4 mb-4 text-center">
        tap in, stats out.
      </div>
    </section>
  );
}
